import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Building2, 
  Plus, 
  Save, 
  Award,
  Star,
  X,
  Shield
} from 'lucide-react'
import apiService from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'react-toastify'
import GovProfileForm from '../../components/enterprise/GovProfileForm'
import ProfilePictureUpload from '../../components/ProfilePictureUpload'

interface VendorProfile {
  id: string
  companyName: string
  firstName: string
  lastName: string
  email: string
  phone: string
  website: string
  avatar: string
  locationCity: string
  locationState: string
  specialties: string[]
  certifications: string[]
  createdAt: string
}

interface VendorProject {
  id: string
  title: string
  description: string
  status: string
  projectType: string
  systemSizeKw: number
  location: string
  completedAt: string
  createdAt: string
}

export default function VendorProfileManagementPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  
  const [activeTab, setActiveTab] = useState<'profile' | 'projects' | 'specialties' | 'certifications' | 'government'>('profile')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  // Profile state
  const [profile, setProfile] = useState<VendorProfile>({
    id: '',
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    website: '',
    avatar: '',
    locationCity: '',
    locationState: '',
    specialties: [],
    certifications: [],
    createdAt: ''
  })
  
  const [projects, setProjects] = useState<VendorProject[]>([])
  const [newSpecialty, setNewSpecialty] = useState('')
  const [newCertification, setNewCertification] = useState('')

  // Fetch gov certification status for tab visibility
  const [_govProfile, _setGovProfile] = useState<{ isGovCertified: boolean, govCertifications: string[], approvedAgencies: string[] }>({ isGovCertified: false, govCertifications: [], approvedAgencies: [] })

  useEffect(() => {
    // Check if user is an enterprise user (supports both new and legacy formats)
    const isEnterpriseUser = (user?.isEnterprise && (user?.role === 'BUYER' || user?.role === 'VENDOR')) || 
                             user?.role === 'ENTERPRISE_BUYER' || user?.role === 'ENTERPRISE_VENDOR'
    
    if (!isEnterpriseUser) {
      navigate('/')
      return
    }
    fetchVendorProfile()
    fetchGovProfile()
  }, [user, navigate])

  const fetchVendorProfile = async () => {
    try {
      setLoading(true)
      const response = await apiService.get(`/api/company-profile/${user?.id}`) as any
      
      if (response.success) {
        const userData = response.data
        setProfile({
          id: userData.id,
          companyName: userData.companyName || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          website: userData.website || '',
          avatar: userData.avatar || '',
          locationCity: userData.locationCity || '',
          locationState: userData.locationState || '',
          specialties: userData.specialties || [],
          certifications: userData.certifications || [],
          createdAt: userData.createdAt || ''
        })
        setProjects(userData.vendorProjects || [])
      }
    } catch (error) {
      console.error('Error fetching vendor profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchGovProfile = async () => {
    try {
      const response = await apiService.get(`/api/company-profile/${user?.id}`) as any
      if (response.success && response.data) {
        _setGovProfile({
          isGovCertified: response.data.isGovCertified || false,
          govCertifications: response.data.govCertifications || [],
          approvedAgencies: response.data.approvedAgencies || []
        })
      }
    } catch (error) {
      _setGovProfile({ isGovCertified: false, govCertifications: [], approvedAgencies: [] })
    }
  }

  // Show the government tab for all enterprise vendors so they can apply
  const showGovernmentTab = true;

  const handleSaveProfile = async () => {
    try {
      setSaving(true)
      const response = await apiService.put(`/api/company-profile/${user?.id}`, {
        companyName: profile.companyName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        website: profile.website,
        avatar: profile.avatar,
        locationCity: profile.locationCity,
        locationState: profile.locationState,
        specialties: profile.specialties,
        certifications: profile.certifications
      }) as any
      
      if (response.success) {
        // alert('Profile updated successfully!')
        toast.success('Profile updated successfully!')
      }
    } catch (error) {
      console.error('Error saving profile:', error)
      alert('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  const addSpecialty = () => {
    if (newSpecialty.trim() && !profile.specialties.includes(newSpecialty.trim())) {
      setProfile({
        ...profile,
        specialties: [...profile.specialties, newSpecialty.trim()]
      })
      setNewSpecialty('')
    }
  }

  const removeSpecialty = (specialty: string) => {
    setProfile({
      ...profile,
      specialties: profile.specialties.filter(s => s !== specialty)
    })
  }

  const addCertification = () => {
    if (newCertification.trim() && !profile.certifications.includes(newCertification.trim())) {
      setProfile({
        ...profile,
        certifications: [...profile.certifications, newCertification.trim()]
      })
      setNewCertification('')
    }
  }

  const removeCertification = (certification: string) => {
    setProfile({
      ...profile,
      certifications: profile.certifications.filter(c => c !== certification)
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Company Profile Management</h1>
              <p className="text-gray-600 text-sm sm:text-base">Manage your company profile and business information</p>
            </div>
            <button
              // onClick={() => navigate(`/company/profile/${user?.id}`)}
              onClick={() => navigate(`/coming-soon`)}
              className="flex items-center justify-center px-4 py-2 text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 w-full sm:w-auto"
            >
              <Building2 className="h-4 w-4 mr-2" />
              <span className="text-sm">View Public Profile</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Tab Selector */}
      <div className="lg:hidden bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="py-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Section
            </label>
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="profile">📋 Profile Info</option>
              <option value="projects">⭐ Projects</option>
              <option value="specialties">🏆 Specialties</option>
              <option value="certifications">🏅 Certifications</option>
              {showGovernmentTab && <option value="government">🏛️ Government Certification</option>}
            </select>
          </div>
        </div>
      </div>

      {/* Desktop Tabs */}
      <div className="hidden lg:block bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {[
              { id: 'profile', label: 'Profile Info', icon: Building2 },
              { id: 'projects', label: 'Projects', icon: Star },
              { id: 'specialties', label: 'Specialties', icon: Award },
              { id: 'certifications', label: 'Certifications', icon: Award },
              ...(showGovernmentTab ? [{ id: 'government', label: 'Government Certification', icon: Shield }] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center px-3 py-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'profile' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {/* Profile Picture Section */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Company Profile Picture
              </label>
              <div className="flex items-center space-x-6">
                <ProfilePictureUpload
                  currentAvatar={profile.avatar}
                  onAvatarChange={(avatarUrl) => {
                    setProfile(prev => ({ ...prev, avatar: avatarUrl }))
                    toast.success('Profile picture updated successfully!')
                  }}
                  size="lg"
                />
                <div className="text-sm text-gray-600">
                  <p className="font-medium">Upload your company logo or profile picture</p>
                  <p className="text-gray-500">This will be displayed in vendor directories and on your public profile</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Basic Information */}
              <div className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={profile.companyName}
                    onChange={(e) => setProfile({ ...profile, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Your company name..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profile.firstName}
                      onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profile.lastName}
                      onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={profile.website}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://yourcompany.com"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={profile.locationCity}
                      onChange={(e) => setProfile({ ...profile, locationCity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={profile.locationState}
                      onChange={(e) => setProfile({ ...profile, locationState: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="contact@yourcompany.com"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Projects</h2>
            {projects.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {projects.map((project) => (
                  <div key={project.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">{project.title}</h3>
                    <p className="text-gray-600 mt-2 text-sm">{project.description}</p>
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-500 gap-2">
                      <span className="truncate">{project.location}</span>
                      <span className="flex-shrink-0">{project.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No projects added yet.</p>
            )}
          </div>
        )}

        {activeTab === 'specialties' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Specialties</h2>
            
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  placeholder="Add a specialty..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  onKeyPress={(e) => e.key === 'Enter' && addSpecialty()}
                />
                <button
                  onClick={addSpecialty}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {profile.specialties.map((specialty, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                >
                  {specialty}
                  <button
                    onClick={() => removeSpecialty(specialty)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'certifications' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Certifications</h2>
            
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  placeholder="Add a certification..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  onKeyPress={(e) => e.key === 'Enter' && addCertification()}
                />
                <button
                  onClick={addCertification}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {profile.certifications.map((certification, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                >
                  {certification}
                  <button
                    onClick={() => removeCertification(certification)}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'government' && (
          <GovProfileForm onSave={fetchVendorProfile} />
        )}
      </div>
    </div>
  )
} 